import { useContext } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Step1PersonalInfo, Step2ProfileInfo, StepIndicator } from './form-steps';
import { useUserInfoForm, InitialUserData } from './hooks/useUserInfoForm';
import { AuthContext } from './AuthProvider';

interface UserInfoFormProps {
  initialDisplayName?: string;
  onComplete: () => void;
  onError: (error: string) => void;
}

export default function UserInfoForm({
  initialDisplayName = '',
  onComplete,
  onError,
}: UserInfoFormProps) {
  const { user: supabaseUser } = useContext(AuthContext);

  // Extract all existing user data for pre-filling
  const getInitialUserData = (): InitialUserData => {
    if (!supabaseUser?.user_metadata) {
      return { displayName: initialDisplayName };
    }

    const metadata = supabaseUser.user_metadata;
    return {
      displayName:
        metadata.full_name || metadata.name || metadata.display_name || initialDisplayName,
      country: metadata.country || '',
      howFoundUs: metadata.how_found_us || '',
      userType: metadata.user_type || '',
      companyName: metadata.company_name || '',
      roleDescription: metadata.role_description || '',
    };
  };

  const {
    currentStep,
    formData,
    loading,
    isCurrentStepValid,
    updateField,
    nextStep,
    prevStep,
    submitForm,
  } = useUserInfoForm(getInitialUserData());

  const handleNext = () => {
    nextStep();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const error = await submitForm();
    if (error) {
      onError(error);
    } else {
      onComplete();
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1PersonalInfo formData={formData} loading={loading} updateField={updateField} />
        );
      case 2:
        return <Step2ProfileInfo formData={formData} loading={loading} updateField={updateField} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-theme-light-page-bg dark:bg-theme-dark-page-bg">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-theme-light-bg dark:bg-theme-dark-bg rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 text-center">
              <h1 className="text-xl font-semibold text-theme-light-text-primary dark:text-theme-dark-text-primary mb-1">
                Complete Your Profile
              </h1>
              <p className="text-sm text-theme-light-text-secondary dark:text-theme-dark-text-secondary">
                Please provide your information to personalize your experience
              </p>
            </div>

            {/* Step Indicator */}
            <div className="px-6 pb-4">
              <StepIndicator currentStep={currentStep} totalSteps={2} />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-6 pb-6">
              {/* Current Step Content */}
              <div>{renderCurrentStep()}</div>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                {/* Back Button */}
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1 || loading}
                  className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    currentStep === 1 || loading
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Back
                </button>

                {/* Next/Submit Button */}
                {currentStep < 2 ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={!isCurrentStepValid || loading}
                    className="flex items-center px-6 py-2 bg-[#E34290] text-white text-sm font-medium rounded-lg hover:bg-[#E34290]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E34290] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={loading || !isCurrentStepValid}
                    className="flex items-center px-6 py-2 bg-[#E34290] text-white text-sm font-medium rounded-lg hover:bg-[#E34290]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E34290] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Complete Profile'
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
